import React from 'react';
import { PROGRAM_PROGRESS_PREVIEW_COLUMNS } from './DisabledDataFramePreview';
import { ProgramProgressToolbarPreview } from '../program/ProgramProgressToolbarPreview';

const ANNUAL_READ_ONLY_COL_CLASSES = [
  'cp-annual-progress-col-seq',
  'cp-annual-progress-col-date',
  'cp-annual-progress-col-weekday',
  'cp-annual-progress-col-period',
  'cp-annual-progress-col-grade',
  'cp-annual-progress-col-semester',
  'cp-annual-progress-col-template',
  'cp-annual-progress-col-program',
  'cp-annual-progress-col-timetable',
  'cp-annual-progress-col-unit',
  'cp-annual-progress-col-learning',
  'cp-annual-progress-col-metric',
  'cp-annual-progress-col-metric',
  'cp-annual-progress-col-metric-period',
  'cp-annual-progress-col-metric-period',
  'cp-annual-progress-col-metric',
  'cp-annual-progress-col-record',
  'cp-annual-progress-col-record',
  'cp-annual-progress-col-record',
  'cp-annual-progress-col-suhub-girog',
  'cp-annual-progress-col-log',
] as const;

const PROGRAM_BODY_TD_CLASSES = PROGRAM_PROGRESS_PREVIEW_COLUMNS.map((c) => c.className ?? '');

function emptyCellInner() {
  return (
    <span className="cp-base-timetable-empty" aria-hidden>
      {' '}
    </span>
  );
}

/** 연간진도표: 읽기 전용 본문 표와 동일한 colgroup·thead, 본문만 비움 */
export const AnnualProgressReadOnlyEmptyTable: React.FC<{ bodyRows?: number }> = ({ bodyRows = 10 }) => (
  <table className="cp-annual-progress-table cp-annual-progress-table--annual-read-only cp-annual-progress-readonly-empty-table">
    <colgroup>
      {ANNUAL_READ_ONLY_COL_CLASSES.map((cls, i) => (
        <col key={`acol-${i}`} className={cls} />
      ))}
    </colgroup>
    <thead>
      <tr>
        <th colSpan={9} className="cp-annual-progress-head-group cp-annual-progress-head-group-base">
          프로그램 일정
        </th>
        <th colSpan={7} className="cp-annual-progress-head-group cp-annual-progress-head-group-learning">
          프로그램 내용
        </th>
        <th colSpan={4} className="cp-annual-progress-head-group cp-annual-progress-head-group-record">
          메모 및 기록
        </th>
        <th className="cp-annual-progress-head-group cp-annual-progress-head-group-log">누가기록</th>
      </tr>
      <tr>
        <th className="cp-annual-progress-col-seq">순</th>
        <th className="cp-annual-progress-col-date">날짜</th>
        <th className="cp-annual-progress-col-weekday">요일</th>
        <th className="cp-annual-progress-col-period" title="해당 수업시간(교시·방과후·활동)">
          교시
        </th>
        <th className="cp-annual-progress-col-grade">학년</th>
        <th className="cp-annual-progress-col-semester">학기</th>
        <th className="cp-annual-progress-col-template">편제</th>
        <th className="cp-annual-progress-col-program">구분</th>
        <th className="cp-annual-progress-col-timetable">시간표</th>
        <th className="cp-annual-progress-col-unit">단원</th>
        <th className="cp-annual-progress-col-learning">프로그램 내용</th>
        <th className="cp-annual-progress-col-metric">쪽수</th>
        <th className="cp-annual-progress-col-metric">보조쪽수</th>
        <th className="cp-annual-progress-col-metric-period">해당 차시</th>
        <th className="cp-annual-progress-col-metric-period">전체 차시</th>
        <th className="cp-annual-progress-col-metric">준비물</th>
        <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill">범교과</th>
        <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill">메모</th>
        <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill">평가</th>
        <th className="cp-annual-progress-col-suhub-girog cp-annual-progress-col-record-fill">수업기록</th>
        <th className="cp-annual-progress-col-log cp-annual-progress-behavior-head-th">
          <button type="button" className="cp-annual-progress-behavior-head-btn" disabled>
            행동특성
          </button>
        </th>
      </tr>
    </thead>
    <tbody>
      {Array.from({ length: bodyRows }, (_, r) => (
        <tr key={r} data-annual-progress-row="true" data-row-index={r}>
          {ANNUAL_READ_ONLY_COL_CLASSES.map((cls, i) => (
            <td key={`${r}-${i}`} className={cls}>
              {emptyCellInner()}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export type ProgramProgressEmptyTableMode = 'track' | 'fixed' | 'project';

/** 편제·고정·프로젝트 프로그램: ProgramProgressTable과 동일한 colgroup·헤더 톤, 본문만 비움 */
export const ProgramProgressEmptyTable: React.FC<{ mode: ProgramProgressEmptyTableMode; bodyRows?: number }> = ({
  mode,
  bodyRows = 10,
}) => {
  const tableModeClass =
    mode === 'fixed' ? 'cp-annual-progress-table-mode-fixed' : mode === 'project' ? 'cp-annual-progress-table-mode-project' : '';

  const headGroupSpans =
    mode === 'fixed'
      ? { base: 10, learning: 6, record: 4 }
      : mode === 'project'
        ? { base: 8, learning: 8, record: 4 }
        : { base: 9, learning: 7, record: 4 };

  return (
    <table
      className={['cp-annual-progress-table', 'cp-program-progress-empty-table', tableModeClass].filter(Boolean).join(' ')}
    >
      <colgroup>
        {PROGRAM_PROGRESS_PREVIEW_COLUMNS.map((c, i) => (
          <col key={i} className={c.colClassName} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th colSpan={headGroupSpans.base} className="cp-annual-progress-head-group cp-annual-progress-head-group-base">
            프로그램 일정
          </th>
          <th colSpan={headGroupSpans.learning} className="cp-annual-progress-head-group cp-annual-progress-head-group-learning">
            프로그램 내용(편집)
          </th>
          <th colSpan={headGroupSpans.record} className="cp-annual-progress-head-group cp-annual-progress-head-group-record">
            메모 및 기록(편집)
          </th>
        </tr>
        <tr>
          <th className="cp-annual-progress-col-seq">순</th>
          <th className="cp-annual-progress-col-date">날짜</th>
          <th className="cp-annual-progress-col-weekday">요일</th>
          <th className="cp-annual-progress-col-period">교시</th>
          <th className="cp-annual-progress-col-grade">학년</th>
          <th className="cp-annual-progress-col-semester">학기</th>
          <th className="cp-annual-progress-col-template">편제</th>
          <th className="cp-annual-progress-col-program">구분</th>
          <th
            className={
              'cp-annual-progress-col-timetable' +
              (mode === 'project' ? ' cp-program-progress-record-head-th' : '')
            }
          >
            {mode === 'project' ? (
              <button type="button" className="cp-program-progress-record-head-btn" disabled tabIndex={-1}>
                시간표
              </button>
            ) : (
              '시간표'
            )}
          </th>
          <th className="cp-annual-progress-col-unit">단원</th>
          <th className="cp-annual-progress-col-learning">학습내용</th>
          <th className="cp-annual-progress-col-metric cp-program-progress-metric-head-th">
            <button type="button" className="cp-program-progress-record-head-btn" disabled>
              쪽수
            </button>
          </th>
          <th className="cp-annual-progress-col-metric">보조쪽수</th>
          <th className="cp-annual-progress-col-metric-period">
            해당
            <br />
            차시
          </th>
          <th className="cp-annual-progress-col-metric-period">
            전체
            <br />
            차시
          </th>
          <th className="cp-annual-progress-col-metric">준비물</th>
          <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill cp-program-progress-record-head-th">
            <button type="button" className="cp-program-progress-record-head-btn" disabled>
              범교과
            </button>
          </th>
          <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill cp-program-progress-record-head-th">
            <button type="button" className="cp-program-progress-record-head-btn" disabled>
              메모
            </button>
          </th>
          <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill cp-program-progress-record-head-th">
            <button type="button" className="cp-program-progress-record-head-btn" disabled>
              평가
            </button>
          </th>
          <th className="cp-annual-progress-col-suhub-girog cp-annual-progress-col-record-fill cp-program-progress-record-head-th">
            <button type="button" className="cp-program-progress-record-head-btn" disabled>
              수업기록
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: bodyRows }, (_, r) => (
          <tr key={r}>
            {PROGRAM_BODY_TD_CLASSES.map((cls, ci) => (
              <td key={ci} className={cls}>
                {emptyCellInner()}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/** 프로그램 페이지용: DisabledDataFramePreview와 동일한 바깥 래퍼 */
export function ProgramProgressEmptyPreviewShell(props: {
  mode: ProgramProgressEmptyTableMode;
  bodyRows?: number;
  /** `ProgramProgressEmptyStateTableCard` 등 실제 카드 안: 바깥 비활성 테두리 없이 스크롤·표만 */
  embedInProgramTableCard?: boolean;
}) {
  const { mode, bodyRows, embedInProgramTableCard } = props;
  const inner = (
    <div className="cp-disabled-data-frame-preview__scroll cp-annual-progress-table-wrap cp-annual-progress-table-wrap--fluid cp-program-progress-scroll-wrap">
      <div
        className={
          'cp-disabled-actual-ui cp-annual-progress-readonly-empty-ui' +
          (embedInProgramTableCard ? ' cp-program-progress-empty-preview--in-table-card' : '')
        }
      >
        <ProgramProgressEmptyTable mode={mode} bodyRows={bodyRows} />
      </div>
    </div>
  );
  if (embedInProgramTableCard) {
    return (
      <div className="cp-program-progress-table-wrap cp-program-progress-table-wrap--readonly cp-program-progress-table-wrap--empty-preview">
        <ProgramProgressToolbarPreview mode={mode} />
        {inner}
      </div>
    );
  }
  return (
    <div className="cp-disabled-data-frame-preview cp-disabled-data-frame-preview--actual" aria-hidden="true">
      {inner}
    </div>
  );
}
